import { useEffect, useState, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { GraduationCap, CheckCircle2, BookOpen, Loader2 } from 'lucide-react'
import { CommunityLayout, GovPageTitle, GovCard, GovEmptyState } from '@/components/community/CommunityLayout'
import { useCommunity } from '@/components/community/CommunityContext'
import { SectionLoading } from '@/components/community/ui'
import { listModules, updateModuleProgress, collectiveLearning, type ModuleResponse } from '@/services/communityApi'

const STATUS_DONE = 'COMPLETED'
const STATUS_PROGRESS = 'IN_PROGRESS'

export default function CommunityLearningPage() {
  const { t } = useTranslation()
  const { community } = useCommunity()
  const [modules, setModules] = useState<ModuleResponse[]>([])
  const [collective, setCollective] = useState<Record<string, number>>({})
  const [loading, setLoading] = useState(true)
  const [busyId, setBusyId] = useState<number | null>(null)

  const load = useCallback(() => {
    if (!community) return
    setLoading(true)
    Promise.allSettled([listModules(), collectiveLearning(community.id)]).then(([m, c]) => {
      if (m.status === 'fulfilled') setModules(m.value)
      if (c.status === 'fulfilled') setCollective(c.value)
      setLoading(false)
    })
  }, [community])

  useEffect(() => { load() }, [load])

  const setStatus = async (mod: ModuleResponse, status: string, pct?: number) => {
    setBusyId(mod.id)
    try {
      await updateModuleProgress(mod.id, { status: status as any, progressPct: pct })
      setModules((p) => p.map((x) => x.id === mod.id ? {
        ...x, myStatus: status, myProgress: status === STATUS_DONE ? 100 : (pct ?? x.myProgress),
      } : x))
    } catch { /* ignore */ } finally { setBusyId(null) }
  }

  if (!community) return null
  const completedCount = collective.COMPLETED ?? collective.completed ?? 0

  return (
    <CommunityLayout>
      <GovPageTitle title={t('community.learning.title')} subtitle={t('community.learning.subtitle')} icon={GraduationCap} />

      <GovCard className="mb-4">
        <p className="text-sm text-gray-600">
          <span className="font-semibold text-gray-900">{t('community.learning.collective')}: </span>
          {t('community.learning.completedBy', { count: completedCount })}
        </p>
      </GovCard>

      {loading ? <SectionLoading rows={4} /> : modules.length === 0 ? (
        <GovEmptyState icon="📚" title={t('community.learning.noModules')} />
      ) : (
        <div className="space-y-3">
          {modules.map((mod) => {
            const done = mod.myStatus === STATUS_DONE
            const inProgress = mod.myStatus === STATUS_PROGRESS
            return (
              <GovCard key={mod.id}>
                <div className="flex items-start gap-3">
                  <div className="w-11 h-11 rounded-xl bg-indigo-50 flex items-center justify-center text-2xl shrink-0" aria-hidden="true">{mod.icon}</div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-semibold text-gray-900">{mod.title}</h3>
                      <span className="text-xs text-gray-400">{t('community.learning.duration', { mins: mod.durationMins })}</span>
                    </div>
                    <p className="text-sm text-gray-600 mt-0.5">{mod.description}</p>

                    {/* Progress bar */}
                    <div className="mt-3 flex items-center gap-3">
                      <div className="flex-1 h-2 rounded-full bg-gray-100 overflow-hidden">
                        <div className={`h-full rounded-full ${done ? 'bg-emerald-500' : 'bg-blue-500'}`} style={{ width: `${done ? 100 : mod.myProgress}%` }} />
                      </div>
                      <span className="text-xs text-gray-500 w-24 text-right">
                        {done ? t('community.learning.completed') : inProgress ? t('community.learning.inProgress') : t('community.learning.notStarted')}
                      </span>
                    </div>

                    {/* Actions */}
                    <div className="mt-3 flex items-center gap-4 flex-wrap">
                      {mod.articleSlug && (
                        <Link to={`/knowledge/${mod.articleSlug}`} className="text-sm font-medium text-blue-600 hover:underline inline-flex items-center gap-1 min-h-[40px]">
                          <BookOpen className="w-4 h-4" aria-hidden="true" />{t('community.learning.readArticle')}
                        </Link>
                      )}
                      {!done && !inProgress && (
                        <button onClick={() => setStatus(mod, STATUS_PROGRESS, 10)} disabled={busyId === mod.id}
                          className="text-sm font-medium text-blue-600 hover:underline disabled:opacity-50 min-h-[40px]">{t('community.learning.start')}</button>
                      )}
                      {inProgress && (
                        <button onClick={() => setStatus(mod, STATUS_PROGRESS, Math.min(90, mod.myProgress + 30))} disabled={busyId === mod.id}
                          className="text-sm font-medium text-blue-600 hover:underline disabled:opacity-50 min-h-[40px]">{t('community.learning.continue')}</button>
                      )}
                      {!done && (
                        <button onClick={() => setStatus(mod, STATUS_DONE, 100)} disabled={busyId === mod.id}
                          className="text-sm font-medium text-emerald-600 hover:underline disabled:opacity-50 inline-flex items-center gap-1 min-h-[40px]">
                          {busyId === mod.id ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> : <CheckCircle2 className="w-4 h-4" aria-hidden="true" />}
                          {t('community.learning.markComplete')}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </GovCard>
            )
          })}
        </div>
      )}
    </CommunityLayout>
  )
}